import { IOS_DEVICE_NAMES } from '../iosDeviceNames';

export function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatShortDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// Two significant digits below 1, so a small reading such as 0.004 does not
// print as 0.0; thousands separators from 1,000.
function roundForDisplay(number) {
  const magnitude = Math.abs(number);
  if (magnitude >= 1000) return Math.round(number).toLocaleString('en-US');
  if (magnitude >= 100) return number.toFixed(0);
  if (magnitude >= 1 || magnitude === 0) return number.toFixed(1);
  return String(Number(number.toPrecision(2)));
}

const UNIT_SUFFIX = {
  percent: '%', points: ' pp', mb: ' MB', ms: ' ms', fps: ' FPS', kb_per_min: ' KB/min',
};

export function formatMetric(value, unit, signed = false) {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  const rounded = roundForDisplay(number);
  const formatted = signed && number > 0 ? `+${rounded}` : rounded;
  return `${formatted}${UNIT_SUFFIX[unit] || ''}`;
}

export function formatRange(range, unit) {
  return `${roundForDisplay(range.low)}–${formatMetric(range.high, unit)}`;
}

function splitDeviceLabel(value) {
  // iOS format: "iPhone14,7 with iOS26.4.2"
  const withSeparator = ' with ';
  const withIndex = value.indexOf(withSeparator);
  if (withIndex !== -1) {
    return { name: value.slice(0, withIndex), os: value.slice(withIndex + withSeparator.length) };
  }
  // Android format: "Google Pixel 9-15.0" (BrowserStack device selector, hyphen-joined)
  const androidMatch = value.match(/^(.+)-(\d[\d.]*)$/);
  if (androidMatch) {
    return { name: androidMatch[1], os: androidMatch[2] };
  }
  return { name: value, os: null };
}

// Display-only: translates a raw iOS hardware identifier (e.g. "iPhone14,7") to its
// marketing name. Devices not in the table (Android, simulators, unreleased models)
// pass through unchanged. Never applied to values used for filtering/matching.
function displayDeviceName(name) {
  return IOS_DEVICE_NAMES[name] || name;
}

function displayOsLabel(os) {
  const ios = String(os).match(/^iOS\s*(\d+)/i);
  if (ios) return `iOS ${ios[1]}`;
  const android = String(os).match(/^(\d+)/);
  if (android) return `Android ${android[1]}`;
  return os;
}

// Combined device + OS display, always hyphen-joined regardless of the
// platform's raw separator (iOS: " with ", Android: "-").
export function displayDeviceLabel(value) {
  if (!value) return value;
  const { name, os } = splitDeviceLabel(value);
  const displayName = displayDeviceName(name);
  return os ? `${displayName} - ${displayOsLabel(os)}` : displayName;
}

export function displayDeviceModel(value) {
  return value ? displayDeviceName(splitDeviceLabel(value).name) : value;
}

export function cleanAppLabel(value) {
  if (!value) return null;
  return String(value).split('/').pop();
}

export function formatScenarioLabel(scenario) {
  if (!scenario || scenario === '-') return null;
  return String(scenario)
    .split(/[_-]+/)
    .map((word) => (word.toLowerCase() === 'sr' ? 'SR' : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}

export function listPhrases(items) {
  return items.flatMap((item, index) => {
    const separator = index === 0 ? null : (index === items.length - 1 ? ' and ' : ', ');
    return [separator, <span key={item.key}>{item.node}</span>];
  }).filter(Boolean);
}
